import { useRouterState } from '@tanstack/react-router';
import type { ImportCandidate } from './contractsMirror.js';
import { MergeScreen } from './MergeScreen.js';

/**
 * The `/clients/merge` route component. The import scan hands its suspected-
 * duplicate groups over via router STATE (`navigate({ to: '/clients/merge',
 * state: { candidates } })`); this route validates that untyped hand-off before
 * rendering, because router state is `unknown` by design and a malformed entry
 * must never reach the merge UI. No state (a direct visit) falls through to the
 * pick-two-by-search screen inside `MergeScreen`.
 */

/** Defensive parse of the router-state hand-off (unknown by design). */
function parseCandidates(value: unknown): ImportCandidate[] {
  if (!Array.isArray(value)) return [];
  const out: ImportCandidate[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const ids = (entry as { ids?: unknown }).ids;
    const names = (entry as { names?: unknown }).names;
    if (
      Array.isArray(ids) &&
      ids.length >= 2 &&
      ids.every((id) => Number.isInteger(id)) &&
      Array.isArray(names) &&
      names.length === ids.length &&
      names.every((name) => typeof name === 'string')
    ) {
      out.push({ ids: [...ids], names: [...names] });
    }
  }
  return out;
}

export function MergeRoute() {
  const routerState = useRouterState({ select: (state) => state.location.state });
  const candidates = parseCandidates(
    (routerState as { candidates?: unknown } | null | undefined)?.candidates,
  );

  // Keyed on the hand-off: a second import navigating here again must reset the
  // working list, and `useState(candidates)` alone would keep the first value.
  return (
    <MergeScreen key={candidates.map((c) => c.ids.join('-')).join('|')} candidates={candidates} />
  );
}
